import { type FC, useEffect, useRef, useState } from "react";
import { connect, type ConnectedProps } from "react-redux";
import dateTimeUtils from "../../../../../utils/dateTimeUtils";

import "./confirmation.scss";
import { type IWizardStep } from "../../../../../interfaces/IWizardStepsManager";
import ContentAssignmentModalTwoSteps from "../../../../../components/assignmentModals/contentAssignmentModal/contentAssignmentModalTwoSteps/ContentAssignmentModalTwoSteps";
import { bindAction, type PacksContextItem } from "../../../../../interfaces";
import { getPeopleSection } from "utils/sendConfirmationUtils";
import useToggle from "hooks/useToggle";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { FeatureFlags } from "featureFlags";
import { type LicenseConfirmationFormRef } from "features/Licensing/ContentAssignmentModalLicensingSteps/LicenseConfirmationModalContent/LicenseConfirmationBlockV2/LicenseConfirmationBlock";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import {
  updateLicensePackInfo,
  reset,
} from "features/Licensing/ContentAssignmentModalLicensingSteps/state/slices/licenseConfirmationInfoSlice";
import PaymentModal from "features/Licensing/Packs/PaymentModal/PaymentModal";

export interface ConfirmationProps extends IWizardStep {
  schedule: Date;
  scheduleTimeZone: string | null;
  communicationId: string;
  communicationTitle: string;
  communicationType: string;
  selectedUserIds: number[];
  selectedGroupIds: number[];
  selectedTemplateGroupIds?: number[];
  promptText: React.ReactElement;
  onConfirm: () => void;
  onCancel: () => void;
  shouldShowLicensingModal: (
    id: string,
    type: string,
    userIds: number[],
    groupIds: number[],
    additionalPurchaseEnabled: boolean,
  ) => Promise<boolean>;
}

export const Confirmation: FC<ConfirmationProps & PropsFromRedux> = (props) => {
  const {
    schedule,
    scheduleTimeZone,
    communicationId,
    communicationTitle,
    communicationType,
    selectedUserIds,
    selectedGroupIds,
    selectedTemplateGroupIds,
    promptText,
    isPurchaseNeeded,
    acceptHandlers,
    onConfirm,
    onCancel,
    shouldShowLicensingModal,
    updateLicensePackInfo,
    resetLicensingModal,
  } = props;

  const [modalVisible, showModal] = useState(false);
  const [showPaymentModal, togglePaymentModal] = useToggle(false);

  const additionalPurchaseEnabled = useFeatureFlag(FeatureFlags.AdditionalPurchase);

  const licenseConfirmationFormRef = useRef<LicenseConfirmationFormRef>({
    getValues: () => [],
  });

  useEffect(() => {
    acceptHandlers?.({
      onFinish: openLicenseModal,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirmLicense = () => {
    if (additionalPurchaseEnabled && isPurchaseNeeded) {
      updateLicensePackInfo(licenseConfirmationFormRef.current.getValues());
      togglePaymentModal();
    } else {
      createSendSession();
    }
    showModal(false);
  };

  const onPaymentSuccess = () => {
    togglePaymentModal();
    createSendSession();
  };

  const createSendSession = () => {
    onConfirm();
    resetLicensingModal();
  };

  const cancelLicenseModal = () => {
    showModal(false);
    onCancel();
    resetLicensingModal();
  };

  const openLicenseModal = async () => {
    const shouldShowModal = await shouldShowLicensingModal(
      communicationId,
      communicationType.toLowerCase(),
      selectedUserIds,
      selectedGroupIds,
      additionalPurchaseEnabled,
    );

    if (shouldShowModal) {
      showModal(true);
    } else {
      onConfirm();
    }
  };

  const getPackContextItem = (): PacksContextItem => {
    return {
      id: communicationId,
      title: communicationTitle,
      type: communicationType,
    };
  };

  const getDateTimeSection = () => {
    return (
      <li>
        On{" "}
        <b>
          {scheduleTimeZone
            ? dateTimeUtils.customFormatDateWithTimeZone(schedule, scheduleTimeZone, "LL")
            : dateTimeUtils.customFormatDate(schedule, "LL")}{" "}
        </b>
        at <b>{dateTimeUtils.dateToTimeString(schedule, "h:mm A z", scheduleTimeZone)}</b>
      </li>
    );
  };

  return (
    <>
      <p className="email-send-session-confirmation-title">
        <b>Almost done!</b>
      </p>
      <p className="email-send-session-confirmation-text" data-testid="email-send-session-confirmation-text">
        <b>{communicationTitle}</b> will be sent to:
        <br />
        <br />
        <ul>
          {getPeopleSection(selectedUserIds, selectedGroupIds, selectedTemplateGroupIds)}
          {getDateTimeSection()}
        </ul>
        <br />
        {promptText}
      </p>
      <ContentAssignmentModalTwoSteps
        showModal={modalVisible}
        flowHasOrigin={false}
        onCancel={cancelLicenseModal}
        onConfirm={confirmLicense}
        packContextItems={[getPackContextItem()]}
        selectedUserIds={selectedUserIds}
        selectedGroupIds={selectedGroupIds}
        skipLicensing={false}
        skipNotifyStep
        notifyStepDisabled
        licenseConfirmationFormRef={licenseConfirmationFormRef}
      />
      {showPaymentModal && props.paymentPacks && (
        <PaymentModal
          paymentPacks={props.paymentPacks}
          onClose={() => {
            cancelLicenseModal();
            togglePaymentModal();
          }}
          onPaymentSuccess={onPaymentSuccess}
        />
      )}
    </>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  return {
    paymentPacks: state.licensing.licenseConfirmationInfo.paymentPacks,
    isPurchaseNeeded: state.licensing?.availableLicenses?.isPurchaseNeeded,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  resetLicensingModal: bindAction(reset, dispatch),
  updateLicensePackInfo: bindAction(updateLicensePackInfo, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(Confirmation);
export default ConnectedComponent;
